import 'swiper/css';
import 'swiper/css/navigation';

import { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { ArrowLeftIcon, ArrowRightIcon, SwiperArrowLeftIcon, SwiperArrowRightIcon } from 'icons';
import { useTranslations } from 'next-intl';
import { EffectCoverflow, Navigation } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { NavigationOptions } from 'swiper/types';

import { useAppDispatch, useAppSelector } from 'store';

import { setUser } from 'modules/global';

import { nextStep, prevStep } from '../store';

const defaultIndex = 17;

const AgeSwiperStep = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const [direction, setDirection] = useState<'horizontal' | 'vertical'>('horizontal');
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null);

  const ages = Array.from({ length: 64 }, (_, i) => i + 7);
  const userBirthday = useAppSelector(state => state.user.birthday); // "YYYY.MM.DD"
  const birthDate = dayjs(userBirthday, 'YYYY.MM.DD');

  const age = birthDate.isValid() ? dayjs().year() - birthDate.year() : defaultIndex;
  const userAgeIndex = ages.indexOf(age);
  const [selectedAge, setSelectedAge] = useState(ages[userAgeIndex >= 0 ? userAgeIndex : defaultIndex]);

  const onContinue = () => {
    const currentBirthday = dayjs(userBirthday, 'YYYY.MM.DD');

    const newBirthday = currentBirthday.isValid()
      ? currentBirthday.set('year', dayjs().year() - selectedAge)
      : dayjs().set('month', 0).set('date', 1).subtract(selectedAge, 'year');

    dispatch(setUser({ birthday: newBirthday.format('YYYY.MM.DD') }));
    dispatch(nextStep());
  };

  useEffect(() => {
    const updateDirection = () => {
      setDirection(window.innerWidth < 640 ? 'vertical' : 'horizontal');
    };

    updateDirection();
    window.addEventListener('resize', updateDirection);
    return () => window.removeEventListener('resize', updateDirection);
  }, []);

  useEffect(() => {
    if (swiperInstance && prevRef.current && nextRef.current && swiperInstance.params.navigation) {
      const navigation = swiperInstance.params.navigation as NavigationOptions;

      navigation.prevEl = prevRef.current;
      navigation.nextEl = nextRef.current;

      swiperInstance.navigation.destroy();
      swiperInstance.navigation.init();
      swiperInstance.navigation.update();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-[94vh] w-full flex-col justify-between p-6 sm:h-full">
      <div>
        <h3 className="mb-2 text-3xl font-semibold">{t('yoshingiz_nechida')}</h3>
        <p className="text-sm">{t('iltimos_aniq_yoshingizni_kiriting')}</p>
      </div>

      <div className="relative flex flex-row items-center justify-center sm:gap-5">
        <button
          ref={prevRef}
          className="z-10 hidden cursor-pointer rounded-full border border-gray-300 bg-gray-100 p-2 shadow-md sm:block"
        >
          <SwiperArrowLeftIcon />
        </button>

        <div className="relative h-[60vh] w-full  max-w-lg sm:h-full">
          {/* Left overlay */}
          <div className="z-9 w-35 pointer-events-none absolute left-0 top-0 hidden h-full bg-gradient-to-r from-white to-transparent sm:block" />

          {/* Right overlay */}
          <div className="z-9 w-35 pointer-events-none absolute right-0 top-0 hidden h-full bg-gradient-to-l from-white to-transparent sm:block" />

          <Swiper
            direction={direction}
            spaceBetween={30}
            loop
            effect="coverflow"
            grabCursor={true}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current
            }}
            onInit={swiper => {
              setSwiperInstance(swiper);
            }}
            onSwiper={setSwiperInstance}
            slidesPerView={4}
            centeredSlides={true}
            initialSlide={userAgeIndex}
            onSlideChange={swiper => setSelectedAge(ages[swiper.realIndex])}
            modules={[Navigation, EffectCoverflow]}
            className="h-100 w-24 sm:h-20 sm:w-full"
            coverflowEffect={{
              rotate: 20,
              stretch: 0,
              depth: 150,
              modifier: 1,
              slideShadows: false
            }}
          >
            {ages.map((age, index) => (
              <SwiperSlide
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                onClick={() => {
                  if (swiperInstance) {
                    swiperInstance.slideToLoop(index);
                  }
                }}
              >
                <div
                  className={`flex h-20 cursor-pointer items-center justify-center rounded-md border !text-4xl
                    ${
                      age === selectedAge
                        ? 'border-2 border-green-600 bg-green-100 text-xl font-bold text-black'
                        : 'border border-gray-300 bg-gray-100 text-gray-400'
                    }`}
                >
                  {age}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <button
          ref={nextRef}
          className="z-10 hidden cursor-pointer rounded-full border border-gray-300 bg-gray-100 p-2 shadow-md sm:block"
        >
          <SwiperArrowRightIcon />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-gray-400">4/8</div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => dispatch(prevStep())}
            className="!py-5.5 !rounded-lg !border-none !border-transparent !bg-gray-100 !px-4 hover:!text-black"
          >
            <div className="mt-0.5 ">
              <ArrowLeftIcon size="18" />
            </div>
            {t('ortga')}
          </Button>
          <Button
            onClick={onContinue}
            disabled={!selectedAge}
            className="!rounded-lg !border-transparent !bg-green-500 !px-5 !py-5 !text-white disabled:!bg-gray-100 disabled:!text-gray-400"
          >
            {t('davom_etish')}
            <div className="mt-0.5 ">
              <ArrowRightIcon />
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgeSwiperStep;
